import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormCheckbox, FormGroup, FormRadioGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';
import { mapRefDataPickList } from 'shared/utils/Helpers';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';

export const PrefabricatedScaffoldingDesignDetails = React.forwardRef((props, ref) => {
  const {
    maxPlatformDutyRating: savedMaxPlatformDutyRating = '',
    material: savedMaterial = '',
    otherMaterial: savedOtherMaterial = '',
    outerDiameter: savedOuterDiameter = '',
    wallThickness: savedWallThickness = '',
    grade: savedGrade = '',
    maxPlatformHeight: savedMaxPlatformHeight = '',
    frameWidth: savedFrameWidth = '',
    frameHeight: savedFrameHeight = '',
    maxDesignHeight: savedMaxDesignHeight = '',
    maxFullyPlankedPlatformLevels: savedMaxFullyPlankedPlatformLevels = '',
    isLoadedSimultaneously: savedIsLoadedSimultaneously = '',
  } = props.value || {};

  const [maxPlatformDutyRating, setMaxPlatformDutyRating] = useState(savedMaxPlatformDutyRating);
  const [material, setMaterialType] = useState(savedMaterial);
  const [otherMaterial, setOtherMaterialType] = useState(savedOtherMaterial);
  const [outerDiameter, setOuterDiameter] = useState(savedOuterDiameter);
  const [wallThickness, setWallThickness] = useState(savedWallThickness);
  const [grade, setGrade] = useState(savedGrade);
  const [maxPlatformHeight, setMaxPlatformHeight] = useState(savedMaxPlatformHeight);
  const [frameWidth, setFrameWidth] = useState(savedFrameWidth);
  const [frameHeight, setFrameHeight] = useState(savedFrameHeight);
  const [maxDesignHeight, setMaxDesignHeight] = useState(savedMaxDesignHeight);
  const [maxFullyPlankedPlatformLevels, setMaxFullyPlankedPlatformLevels] = useState(
    savedMaxFullyPlankedPlatformLevels,
  );
  const [isLoadedSimultaneously, setIsLoadedSimultaneously] = useState(savedIsLoadedSimultaneously);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const REF_CONSTANTS = PlantDesignRefData.prefabricatedScaffolding;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.prefabricatedScaffolding;

  const [isFrameOrTowerFrame, setIsFrameOrTowerFrame] = useState(undefined);
  const [isModular, setIsModular] = useState(undefined);

  const materialOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.materialOptions]);
  const maxPlatformDutyRatingOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.maxPlatformDutyRatingOptions]);

  useEffect(() => {
    if (!props.subType) return;

    setIsFrameOrTowerFrame(props.subType === REF_CONSTANTS.frameType || props.subType === REF_CONSTANTS.towerFrameType);
    setIsModular(props.subType === REF_CONSTANTS.modularType);
  }, [props.subType]);

  useEffect(() => {
    if (isFrameOrTowerFrame === false) {
      setMaxPlatformHeight('');
      setFrameWidth('');
      setFrameHeight('');
    }
  }, [isFrameOrTowerFrame]);

  useEffect(() => {
    if (isModular === false) {
      setMaxDesignHeight('');
      setMaxFullyPlankedPlatformLevels('');
      setIsLoadedSimultaneously(undefined);
    }
  }, [isModular]);

  const validators = [
    {
      fieldName: 'maxPlatformDutyRating',
      fieldValue: maxPlatformDutyRating,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'material',
      fieldValue: material,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'otherMaterial',
      fieldValue: otherMaterial,
      isValid: (v) => material !== REF_CONSTANTS.otherMaterial || !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'outerDiameter',
      fieldValue: outerDiameter,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'wallThickness',
      fieldValue: wallThickness,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'grade',
      fieldValue: grade,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxPlatformHeight',
      fieldValue: maxPlatformHeight,
      isValid: (v) => !isFrameOrTowerFrame || isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'frameWidth',
      fieldValue: frameWidth,
      isValid: (v) => !isFrameOrTowerFrame || isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'frameHeight',
      fieldValue: frameHeight,
      isValid: (v) => !isFrameOrTowerFrame || isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxDesignHeight',
      fieldValue: maxDesignHeight,
      isValid: (v) => !isModular || isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxFullyPlankedPlatformLevels',
      fieldValue: maxFullyPlankedPlatformLevels,
      isValid: (v) => !isModular || isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isCurrentComponentValid);
        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let details = {
      maxPlatformDutyRating,
      material,
      otherMaterial,
      outerDiameter,
      wallThickness,
      grade,
    };

    if (material !== REF_CONSTANTS.otherMaterial) delete details.otherMaterial;

    if (isFrameOrTowerFrame) {
      details = { ...details, maxPlatformHeight, frameWidth, frameHeight };
    }

    if (isModular) {
      details = { ...details, maxDesignHeight, maxFullyPlankedPlatformLevels, isLoadedSimultaneously };
    }

    props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    maxPlatformDutyRating,
    material,
    otherMaterial,
    outerDiameter,
    wallThickness,
    grade,
    maxDesignHeight,
    maxPlatformHeight,
    frameWidth,
    frameHeight,
    maxFullyPlankedPlatformLevels,
    isLoadedSimultaneously,
  ]);

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <FormRadioGroup
        id="maxPlatformDutyRating"
        name="maxPlatformDutyRating"
        vertical={true}
        options={maxPlatformDutyRatingOptions}
        onChange={setMaxPlatformDutyRating}
        value={maxPlatformDutyRating}
        errorMessage={errors?.maxPlatformDutyRating?.errorMessage}
        hasError={showErrors && errors?.maxPlatformDutyRating?.invalid}
        legend={PAGE_CONSTANTS.maxPlatformDutyRating.label}
        margin={{ top: 'lg' }}
      />
      <RadioOrDropdownSelector
        name="material"
        options={materialOptions}
        title={PAGE_CONSTANTS.material.label}
        helpMessage={PAGE_CONSTANTS.materialHelpText}
        errorMessage={errors?.material?.errorMessage}
        hasError={showErrors && errors?.material?.invalid}
        onChange={(v) => {
          setMaterialType(v);
          setOtherMaterialType('');
        }}
        value={material}
        showExtraInput={material === REF_CONSTANTS.otherMaterial}
        extraInputType="textarea"
        onChangeExtraInput={setOtherMaterialType}
        extraInputValue={otherMaterial}
        extraInputErrorMessage={errors?.otherMaterial?.errorMessage}
        extraInputHasError={showErrors && errors?.otherMaterial?.invalid}
        extraInputTitle={PAGE_CONSTANTS.otherMaterial.label}
        margin={{ top: 'lg' }}
        maxOptionsLengthForRadio={5}
      />
      <FormGroup
        id="outerDiameter"
        label={PAGE_CONSTANTS.outerDiameter.label}
        isRequired
        errorMessage={errors?.outerDiameter?.errorMessage}
        hasError={showErrors && errors?.outerDiameter?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input suffix="mm" onChange={(e) => setOuterDiameter(e.target.value)} value={outerDiameter} inputWidth="md" />
      </FormGroup>
      <FormGroup
        id="wallThickness"
        label={PAGE_CONSTANTS.wallThickness.label}
        isRequired
        errorMessage={errors?.wallThickness?.errorMessage}
        hasError={showErrors && errors?.wallThickness?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input suffix="mm" onChange={(e) => setWallThickness(e.target.value)} value={wallThickness} inputWidth="md" />
      </FormGroup>
      <FormGroup
        id="grade"
        label={PAGE_CONSTANTS.grade.label}
        isRequired
        errorMessage={errors?.grade?.errorMessage}
        hasError={showErrors && errors?.grade?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'lg' }}
      >
        <Input suffix="MPa" onChange={(e) => setGrade(e.target.value)} value={grade} inputWidth="md" />
      </FormGroup>
      {isFrameOrTowerFrame && (
        <>
          <FormGroup
            id="maxPlatformHeight"
            label={PAGE_CONSTANTS.maxPlatformHeight.label}
            isRequired
            errorMessage={errors?.maxPlatformHeight?.errorMessage}
            hasError={showErrors && errors?.maxPlatformHeight?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'lg' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.maxPlatformHeight.suffix}
              onChange={(e) => setMaxPlatformHeight(e.target.value)}
              value={maxPlatformHeight}
              inputWidth="md"
            />
          </FormGroup>

          <FormGroup
            id="frameWidth"
            label={PAGE_CONSTANTS.frameWidth.label}
            isRequired
            errorMessage={errors?.frameWidth?.errorMessage}
            hasError={showErrors && errors?.frameWidth?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'lg' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.frameWidth.suffix}
              onChange={(e) => setFrameWidth(e.target.value)}
              value={frameWidth}
              inputWidth="md"
            />
          </FormGroup>

          <FormGroup
            id="frameHeight"
            label={PAGE_CONSTANTS.frameHeight.label}
            isRequired
            errorMessage={errors?.frameHeight?.errorMessage}
            hasError={showErrors && errors?.frameHeight?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'lg' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.frameHeight.suffix}
              onChange={(e) => setFrameHeight(e.target.value)}
              value={frameHeight}
              inputWidth="md"
            />
          </FormGroup>
        </>
      )}
      {isModular && (
        <>
          <FormGroup
            id="maxDesignHeight"
            label={PAGE_CONSTANTS.maxDesignHeight.label}
            isRequired
            errorMessage={errors?.maxDesignHeight?.errorMessage}
            hasError={showErrors && errors?.maxDesignHeight?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'lg' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.maxDesignHeight.suffix}
              onChange={(e) => setMaxDesignHeight(e.target.value)}
              value={maxDesignHeight}
              inputWidth="md"
            />
          </FormGroup>

          <FormGroup
            id="maxFullyPlankedPlatformLevels"
            label={PAGE_CONSTANTS.maxFullyPlankedPlatformLevels.label}
            isRequired
            errorMessage={errors?.maxFullyPlankedPlatformLevels?.errorMessage}
            hasError={showErrors && errors?.maxFullyPlankedPlatformLevels?.invalid}
            margin={{ top: 'lg' }}
          >
            <Input
              onChange={(e) => setMaxFullyPlankedPlatformLevels(e.target.value)}
              value={maxFullyPlankedPlatformLevels}
              inputWidth="md"
            />
          </FormGroup>

          <FormCheckbox
            label={PAGE_CONSTANTS.isLoadedSimultaneously.label}
            onChange={() => setIsLoadedSimultaneously(isLoadedSimultaneously === 'yes' ? '' : 'yes')}
            checked={isLoadedSimultaneously === 'yes'}
            name="isLoadedSimultaneously"
            value=""
            margin={{ top: 'lg' }}
          />
        </>
      )}
    </>
  );
});

PrefabricatedScaffoldingDesignDetails.displayName = 'PrefabricatedScaffoldingDesignDetails';

PrefabricatedScaffoldingDesignDetails.propTypes = {
  value: PropTypes.object,
  subType: PropTypes.string,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
